import React, { useEffect, useMemo, useState } from "react";
import SchoolDatePicker from "../../components/SchoolDatePicker/SchoolDatePicker";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import SchoolService from "./Locality/Service/schoolService";
import EnquiryService from "./Locality/Service/enquiryService";
import AcademicYearService from "./Locality/Service/academicYearService";
import { useDuplicateMobile } from "./crm/crmUi";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    background: "#f6f7fb",
    padding: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
  },
  shell: {
    maxWidth: 1000,
    margin: "0 auto",
  },
  headerCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
  },
  brandRow: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      textAlign: "center",
      justifyItems: "center",
    },
  },
  logo: {
    width: 54,
    height: 54,
    borderRadius: 12,
    objectFit: "cover",
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff",
  },
  title: {
    fontWeight: 800,
    letterSpacing: 0.2,
  },
  subtitle: {
    color: "rgba(0,0,0,0.65)",
    marginTop: theme.spacing(0.5),
  },
  yearChip: {
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 999,
    background: "rgba(25, 118, 210, 0.10)",
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  formCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
  },
  sectionTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  helper: {
    color: "rgba(0,0,0,0.6)",
    marginBottom: theme.spacing(2),
  },
  footer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  primaryButton: {
    borderRadius: 12,
    height: 44,
    fontWeight: 800,
  },
  danger: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
}));

const ADMISSION_OPTIONS = ["Regular school", "Summer camp"];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const tenDigitRegex = /^[0-9]{10}$/;

function formatDDMMYYYY(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function isoToDDMMYYYY(iso) {
  const s = String(iso || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}


export default function EnquiryFormPublicPage() {
  const classes = useStyles();
  const { schoolId } = useParams();

  const [schoolName, setSchoolName] = useState("");
  const [schoolLogo, setSchoolLogo] = useState("");
  const [isSchoolLoading, setIsSchoolLoading] = useState(true);
  const [schoolError, setSchoolError] = useState("");
  const [classOptions, setClassOptions] = useState([]);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearId, setAcademicYearId] = useState("");
  const [isAcademicYearLoading, setIsAcademicYearLoading] = useState(true);
  const [academicYearError, setAcademicYearError] = useState("");

  const systemDate = useMemo(() => new Date(), []);
  const systemDateText = useMemo(() => formatDDMMYYYY(systemDate), [systemDate]);

  const [values, setValues] = useState({
    studentName: "",
    dob: "",
    gender: "",
    fatherName: "",
    fatherContactNumber: "",
    motherName: "",
    motherContactNumber: "",
    email: "",
    classOrAgeGroup: "",
    admissionSoughtFor: "",
    additionalInformation: "",
  });

  // Optional custom fields the school added in Enquiry Form admin (up to 5).
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setIsSchoolLoading(true);
    setSchoolError("");

    SchoolService.getSchooleRegistrationById(schoolId)
      .then((res) => {
        if (!mounted) return;
        setSchoolName(res?.schooleName || "Enquiry Form");
        setSchoolLogo(res?.logo || "");
        const rooms = Array.isArray(res?.classrooms) ? res.classrooms : [];
        setClassOptions(rooms.filter(Boolean));
      })
      .catch(() => {
        if (!mounted) return;
        setSchoolError("Unable to load school details. Please check the link and try again.");
        setClassOptions([]);
      })
      .finally(() => {
        if (!mounted) return;
        setIsSchoolLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [schoolId]);

  useEffect(() => {
    let mounted = true;
    EnquiryService.getFormConfig(schoolId)
      .then((res) => {
        if (!mounted) return;
        setCustomFields(Array.isArray(res?.customFields) ? res.customFields : []);
      })
      .catch(() => {
        if (mounted) setCustomFields([]);
      });
    return () => {
      mounted = false;
    };
  }, [schoolId]);

  useEffect(() => {
    let mounted = true;
    setIsAcademicYearLoading(true);
    setAcademicYearError("");

    (async () => {
      // The running year and future years from Academic Master (same list as the CRM).
      let options = [];
      let defaultName = "";
      try {
        const res = await AcademicYearService.getEnquiryAcademicYears();
        options = (res?.options || []).map((o) => ({ _id: o._id, acadamicYear: o.name, upcoming: o.upcoming }));
        defaultName = res?.defaultYear || "";
      } catch {
        // leave empty: the error below asks to try later
      }

      if (!mounted) return;

      if (options.length === 0) {
        setAcademicYearOptions([]);
        setAcademicYearId("");
        setAcademicYearError(
          "No current or upcoming academic year is configured in Academic Master."
        );
        setIsAcademicYearLoading(false);
        return;
      }

      setAcademicYearOptions(options);
      const defaultRow = options.find((row) => row.acadamicYear === defaultName) || options[0];
      setAcademicYearId(String(defaultRow._id));
      setAcademicYearError("");
      setIsAcademicYearLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedAcademicYear = useMemo(() => {
    const row = academicYearOptions.find(
      (opt) => String(opt._id) === String(academicYearId)
    );
    return String(row?.acadamicYear || "").trim();
  }, [academicYearOptions, academicYearId]);

  const academicYearDisplay = isAcademicYearLoading
    ? "Loading..."
    : selectedAcademicYear || "—";

  const handleAcademicYearChange = (event) => {
    setSubmitError("");
    setSubmitSuccess("");
    setAcademicYearId(event.target.value);
    setTouched((t) => ({ ...t, academicYearId: true }));
  };

  // Same parent already enquired? Checked while typing; the server checks again on submit.
  const checkMobile = (mobile) => EnquiryService.checkPublicMobile(schoolId, mobile);
  const fatherDuplicate = useDuplicateMobile(values.fatherContactNumber, checkMobile, Boolean(schoolId));
  const motherDuplicate = useDuplicateMobile(values.motherContactNumber, checkMobile, Boolean(schoolId));

  const errors = useMemo(() => {
    const e = {};
    if (!values.studentName?.trim()) e.studentName = "Student Name is required";
    if (!values.dob?.trim()) e.dob = "DOB is required";
    if (!values.gender) e.gender = "Gender is required";

    if (!values.fatherName?.trim()) e.fatherName = "Father name is required";
    if (!values.fatherContactNumber?.trim()) e.fatherContactNumber = "Father contact number is required";
    else if (!tenDigitRegex.test(values.fatherContactNumber.trim())) e.fatherContactNumber = "Enter 10 digit mobile number";

    if (!values.motherName?.trim()) e.motherName = "Mother name is required";
    if (!values.motherContactNumber?.trim()) e.motherContactNumber = "Mother contact number is required";
    else if (!tenDigitRegex.test(values.motherContactNumber.trim())) e.motherContactNumber = "Enter 10 digit mobile number";

    if (!values.email?.trim()) e.email = "Email is required";
    else if (!emailRegex.test(values.email.trim())) e.email = "Enter a valid email";
    if (!academicYearId) e.academicYearId = "Academic year is required";
    return e;
  }, [values, academicYearId]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleChange = (key) => (event) => {
    const next = event?.target?.value;
    setSubmitError("");
    setSubmitSuccess("");

    if (key === "fatherContactNumber" || key === "motherContactNumber") {
      const digitsOnly = String(next || "").replace(/[^0-9]/g, "").slice(0, 10);
      setValues((prev) => ({ ...prev, [key]: digitsOnly }));
      return;
    }
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  const markTouched = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  useEffect(() => {
    if (!submitSuccess) return undefined;
    const t = setTimeout(() => setSubmitSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  const onSubmit = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setTouched((t) => ({
      ...t,
      studentName: true,
      dob: true,
      gender: true,
      fatherName: true,
      fatherContactNumber: true,
      motherName: true,
      motherContactNumber: true,
      email: true,
      academicYearId: true,
    }));

    if (hasErrors) {
      setSubmitError("Please fill all mandatory fields.");
      return;
    }
    if (fatherDuplicate || motherDuplicate) {
      setSubmitError(fatherDuplicate || motherDuplicate);
      return;
    }

    if (!academicYearId || !selectedAcademicYear) {
      setSubmitError(
        academicYearError || "Academic year is not available. Please try again later."
      );
      return;
    }

    try {
      setIsSubmitting(true);
      await EnquiryService.submitPublicEnquiry(schoolId, {
        ...values,
        dob: isoToDDMMYYYY(values.dob),
        academicYear: selectedAcademicYear,
        academicYearId,
        customFields: customFields.map((f) => ({
          key: f.key,
          value: String(customValues[f.key] || "").trim(),
        })),
      });
      setSubmitSuccess("Enquiry submitted successfully.");
      setValues({
        studentName: "",
        dob: "",
        gender: "",
        fatherName: "",
        fatherContactNumber: "",
        motherName: "",
        motherContactNumber: "",
        email: "",
        classOrAgeGroup: "",
        admissionSoughtFor: "",
        additionalInformation: "",
      });
      setCustomValues({});
      setTouched({});
    } catch (err) {
      // The server's message (e.g. "already registered") beats axios' generic one.
      setSubmitError(
        err?.response?.data?.message || err?.message || "Failed to submit enquiry. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.shell}>
        <Card className={classes.headerCard}>
          <div className={classes.brandRow}>
            <Box display="flex" alignItems="center" justifyContent="center">
              {schoolLogo ? (
                <img src={schoolLogo} alt="School Logo" className={classes.logo} />
              ) : (
                <Box className={classes.logo} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="textSecondary">
                    Logo
                  </Typography>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="h5" className={classes.title}>
                {isSchoolLoading ? "Loading..." : schoolName || "Enquiry Form"}
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Academic year {academicYearDisplay} • Date of application: {systemDateText}
              </Typography>
              {academicYearError && !isAcademicYearLoading ? (
                <Typography variant="body2" className={classes.danger} style={{ marginTop: 8 }}>
                  {academicYearError}
                </Typography>
              ) : null}
              {schoolError ? (
                <Typography variant="body2" className={classes.danger} style={{ marginTop: 8 }}>
                  {schoolError}
                </Typography>
              ) : null}
            </Box>
          </div>
        </Card>

        <Card className={classes.formCard}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Mandatory details
          </Typography>
          <Typography variant="body2" className={classes.helper}>
            Fields marked as mandatory must be filled before submitting.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                variant="outlined"
                error={Boolean(touched.academicYearId && errors.academicYearId)}
                disabled={isAcademicYearLoading || academicYearOptions.length === 0}
              >
                <InputLabel
                  id="enquiry-academic-year-label"
                  shrink={Boolean(academicYearId) || isAcademicYearLoading}
                >
                  Academic Year *
                </InputLabel>
                <Select
                  labelId="enquiry-academic-year-label"
                  id="enquiry-academic-year"
                  value={academicYearId || ""}
                  onChange={handleAcademicYearChange}
                  onBlur={markTouched("academicYearId")}
                  label="Academic Year *"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return isAcademicYearLoading ? "Loading..." : "";
                    }
                    const row = academicYearOptions.find(
                      (opt) => String(opt._id) === String(selected)
                    );
                    return row?.acadamicYear || "";
                  }}
                >
                  {academicYearOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      <em>
                        {isAcademicYearLoading
                          ? "Loading..."
                          : "No academic year available"}
                      </em>
                    </MenuItem>
                  ) : (
                    academicYearOptions.map((row) => (
                      <MenuItem key={String(row._id)} value={String(row._id)}>
                        {row.acadamicYear}
                        {row.upcoming ? " (upcoming)" : ""}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {touched.academicYearId && errors.academicYearId ? (
                  <FormHelperText>{errors.academicYearId}</FormHelperText>
                ) : academicYearError ? (
                  <FormHelperText error>{academicYearError}</FormHelperText>
                ) : (
                  <FormHelperText>
                    {isAcademicYearLoading
                      ? "Loading academic years..."
                      : selectedAcademicYear
                      ? `Selected: ${selectedAcademicYear} (active year by default)`
                      : "Current active year is selected by default"}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Student Name *"
                value={values.studentName}
                onChange={handleChange("studentName")}
                onBlur={markTouched("studentName")}
                error={Boolean(touched.studentName && errors.studentName)}
                helperText={touched.studentName && errors.studentName ? errors.studentName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SchoolDatePicker
                label="DOB *"
                value={values.dob}
                onChange={(iso) => handleChange("dob")({ target: { value: iso } })}
                onBlur={markTouched("dob")}
                error={Boolean(touched.dob && errors.dob)}
                helperText={touched.dob && errors.dob ? errors.dob : " "}
                maxDate={new Date().toISOString().slice(0, 10)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl error={Boolean(touched.gender && errors.gender)} component="fieldset">
                <FormLabel component="legend">Gender * (Male / Female)</FormLabel>
                <RadioGroup row value={values.gender} onChange={handleChange("gender")} onBlur={markTouched("gender")}>
                  <FormControlLabel value="Male" control={<Radio />} label="Male" />
                  <FormControlLabel value="Female" control={<Radio />} label="Female" />
                </RadioGroup>
                <FormHelperText>{touched.gender && errors.gender ? errors.gender : " "}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father name *"
                value={values.fatherName}
                onChange={handleChange("fatherName")}
                onBlur={markTouched("fatherName")}
                error={Boolean(touched.fatherName && errors.fatherName)}
                helperText={touched.fatherName && errors.fatherName ? errors.fatherName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father contact number *"
                value={values.fatherContactNumber}
                onChange={handleChange("fatherContactNumber")}
                onBlur={markTouched("fatherContactNumber")}
                error={Boolean((touched.fatherContactNumber && errors.fatherContactNumber) || fatherDuplicate)}
                helperText={(touched.fatherContactNumber && errors.fatherContactNumber) || fatherDuplicate || " "}
                variant="outlined"
                autoComplete="off"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother name *"
                value={values.motherName}
                onChange={handleChange("motherName")}
                onBlur={markTouched("motherName")}
                error={Boolean(touched.motherName && errors.motherName)}
                helperText={touched.motherName && errors.motherName ? errors.motherName : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother contact number *"
                value={values.motherContactNumber}
                onChange={handleChange("motherContactNumber")}
                onBlur={markTouched("motherContactNumber")}
                error={Boolean((touched.motherContactNumber && errors.motherContactNumber) || motherDuplicate)}
                helperText={(touched.motherContactNumber && errors.motherContactNumber) || motherDuplicate || " "}
                variant="outlined"
                autoComplete="off"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email *"
                value={values.email}
                onChange={handleChange("email")}
                onBlur={markTouched("email")}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email ? errors.email : " "}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "18px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Optional details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Classes / Age groups</InputLabel>
                <Select value={values.classOrAgeGroup} onChange={handleChange("classOrAgeGroup")} label="Classes / Age groups">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {classOptions.length === 0 ? (
                    <MenuItem disabled value="__no_classes__">
                      No classes found
                    </MenuItem>
                  ) : (
                    classOptions.map((c) => {
                      const name = c?.className || c?.name || "";
                      if (!name) return null;
                      return (
                        <MenuItem key={c?._id || name} value={name}>
                          {name}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {classOptions.length === 0 ? (
                  <FormHelperText>
                    Classes are not configured for this school.
                  </FormHelperText>
                ) : (
                  <FormHelperText> </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Admission sought for</InputLabel>
                <Select value={values.admissionSoughtFor} onChange={handleChange("admissionSoughtFor")} label="Admission sought for">
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {ADMISSION_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional information"
                value={values.additionalInformation}
                onChange={handleChange("additionalInformation")}
                variant="outlined"
                multiline
                rows={4}
                autoComplete="off"
              />
            </Grid>

            {customFields.map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <TextField
                  fullWidth
                  label={field.label}
                  value={customValues[field.key] || ""}
                  onChange={(e) => {
                    const next = e?.target?.value || "";
                    setSubmitError("");
                    setSubmitSuccess("");
                    setCustomValues((prev) => ({ ...prev, [field.key]: next }));
                  }}
                  variant="outlined"
                  autoComplete="off"
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>
            ))}
          </Grid>

          {submitError ? (
            <Typography variant="body2" className={classes.danger} style={{ marginTop: 12 }}>
              {submitError}
            </Typography>
          ) : null}

          <div className={classes.footer}>
            <Button
              className={classes.primaryButton}
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={isSubmitting || isSchoolLoading || Boolean(schoolError)}
            >
              {isSubmitting ? (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <CircularProgress size={18} style={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : (
                "Submit enquiry form"
              )}
            </Button>
          </div>
        </Card>
      </div>

      <Snackbar
        open={Boolean(submitSuccess)}
        autoHideDuration={2500}
        onClose={() => setSubmitSuccess("")}
        message={submitSuccess}
      />
    </div>
  );
}

